import { useEffect, useContext, FC } from 'react';
import { splitToken } from 'dibs-cookie-jar/exports/splitToken';

import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';
import {
    hasValidUserSessionGeoInfo,
    getUserSessionCountryCode,
} from 'dibs-regional-info/exports/regionalInfoHelpers';

import { setItemNotAvailable } from '../../../helpers/checkoutStorage';
import { isNegotiableOrder, getParamFromUrl } from '../../../helpers/checkoutRedirects';
import { isGuestCheckoutV2Active } from '../../../helpers/guestCheckoutAbTestHelpers';
import { isLoggedIn, isUserCurrent, hasAnyTradeStatus } from '../../../helpers/checkoutUser';
import {
    reloadCheckout,
    goToMessageCenter,
    goToOMT,
    goToItemCategory,
} from '../../../helpers/checkoutRedirects';
import canMakePurchase from '../../../helpers/canMakePurchase';
import { getGuestUserId } from '../../../helpers/checkoutGuestUserToken';

import { useRendererQueryLoader } from './useRendererQueryLoader';
import { deferActionIfInBackground } from '../../../prerender/deferActionIfInBackground';

import { useCheckoutSelector } from '../../../reducers/root';
import { CheckoutServerVarsContext } from '../../shared/CheckoutServerVarsContext';

import { checkoutItemBuyerQuery } from '../../../queries/checkoutItemBuyer';
import { checkoutItemBuyerQuery as checkoutItemBuyerQueryType } from '../../../queries/__generated__/checkoutItemBuyerQuery.graphql';

// components
import CheckoutGuestBuyerQueryRenderer from './CheckoutGuestBuyerQueryRenderer';
import CheckoutErrorPage from '../CheckoutErrorPage';

import pageWrapperStyles from '../styles/checkoutPageWrapper.scss';

function redirectToCategoryPage(item: Viewer['item']): void {
    const title = item?.title;
    const browseUrl = item?.classification?.categories?.[0]?.category?.browseUrl;
    setItemNotAvailable(title);
    goToItemCategory(browseUrl);
}

type Viewer = checkoutItemBuyerQueryType['response']['viewer'];

type Props = {
    itemId: string;
};

const CheckoutItemBuyerQueryRenderer: FC<Props> = props => {
    const { itemId } = props;
    const storeBuyerId = useCheckoutSelector(state => state.transaction.buyerId);
    const skuId = useCheckoutSelector(state => state.transaction.skuId);
    const isReadyAfterItemBuyerFetch = useCheckoutSelector(
        state => state.ui.isReadyAfterItemBuyerFetch
    );
    const hasTradeStatus =
        useCheckoutSelector(state => state.tradeStatus.hasTradeStatus) || hasAnyTradeStatus();
    const serverVarsContext = useContext(CheckoutServerVarsContext);
    const isMobile = serverVarsContext.isMobile;

    const [fetchQuery, data, loadingState, error] =
        useRendererQueryLoader<checkoutItemBuyerQueryType>(checkoutItemBuyerQuery);

    // If the buyerId in the redux store is from a guest user, do not attempt to fetch that user's data
    const buyerId = !!storeBuyerId && storeBuyerId === getGuestUserId() ? null : storeBuyerId;
    const emailToken = getParamFromUrl('emailToken') || '';
    const isAcceptOfferFlow = getParamFromUrl('acceptOffer') === 'true';
    const emailUserId = splitToken(emailToken);

    useEffect(() => {
        if (!isReadyAfterItemBuyerFetch) {
            fetchQuery({
                itemId,
                skuIds: skuId ? [skuId] : [],
                skuId,
                itemIds: [itemId],
                emailUserId: emailUserId,
                hasEmailUserId:
                    !!emailUserId &&
                    isAcceptOfferFlow &&
                    !isLoggedIn() &&
                    isGuestCheckoutV2Active(),
                buyerId: buyerId || '',
                buyerIds: [buyerId],
                hasBuyerId: !!buyerId,
                userIds: [buyerId],
                hasUserId: !!buyerId,
                hasTradeStatus,
                isMobile,
                userCountryCode: getUserSessionCountryCode(),
                hasSessionUserGeoInfo: hasValidUserSessionGeoInfo(),
            });
        }
    }, [
        buyerId,
        fetchQuery,
        hasTradeStatus,
        isMobile,
        itemId,
        skuId,
        isReadyAfterItemBuyerFetch,
        emailUserId,
        isAcceptOfferFlow,
    ]);

    useEffect(() => {
        if (!data?.viewer) {
            /**
             * Do nothing, we're still loading stuff
             */
            return;
        }
        const viewer = data.viewer;
        const item = viewer.item;
        const buyer = viewer.user;
        const preCheckoutTransaction =
            data?.viewer?.preCheckoutTransaction?.edges?.[0]?.node || null;
        const mostRecentOffer = preCheckoutTransaction?.mostRecentOffer;
        /**
         * `isDealerInitiated` was never available in the old checkout, temporarily bring this bug
         * back, until ECOM team figures out what's the best fix.
         */
        // @ts-expect-error
        const { canUseOfferForCheckout, isDealerInitiated } = mostRecentOffer || {};
        const existingTransactionId = viewer?.existingTransaction?.edges?.[0]?.node?.serviceId;

        if (canUseOfferForCheckout && isDealerInitiated && isNegotiableOrder()) {
            goToMessageCenter(buyerId);
        } else if (existingTransactionId) {
            goToOMT(existingTransactionId);
        } else if (!canMakePurchase(item, buyer, preCheckoutTransaction)) {
            if (!buyerId) {
                deferActionIfInBackground(() => {
                    authModalLoader
                        .show({
                            action: authModalLoader.constants.LOGIN,
                            flow: authModalLoader.constants.DEFAULT_FLOW,
                        })
                        .then((resp: $TSFixMe) => {
                            if (!resp.err) {
                                reloadCheckout();
                            } else {
                                redirectToCategoryPage(item);
                            }
                        });
                });
            } else {
                deferActionIfInBackground(() => {
                    redirectToCategoryPage(item);
                });
            }
        } else if (!isUserCurrent(buyerId) && isLoggedIn()) {
            reloadCheckout();
        }
    }, [buyerId, data]);

    const viewer = data?.viewer || null;
    const buyer = viewer?.user || null;
    const emailUser = viewer?.emailUser || null;
    const item = viewer?.item || null;
    const preCheckoutTransaction = data?.viewer?.preCheckoutTransaction?.edges?.[0]?.node || null;

    if (loadingState === 'error') {
        return <CheckoutErrorPage error={error} />;
    }

    return (
        <div id="checkout" className={pageWrapperStyles.wrapper}>
            <CheckoutGuestBuyerQueryRenderer
                {...{
                    emailUser,
                    viewer,
                    item,
                    buyer,
                    preCheckoutTransaction,
                }}
            />
        </div>
    );
};

export default CheckoutItemBuyerQueryRenderer;
